/**
 * Timezone utility functions for converting timestamps based on device location
 */

/**
 * Convert UTC timestamp to local timezone
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @param {string} timezone - IANA timezone string (e.g., 'America/New_York', 'Europe/Prague')
 * @param {object} options - Formatting options
 * @returns {string} Formatted date string in local timezone
 */
export function convertToLocalTimezone(utcTimestamp, timezone = 'UTC', options = {}) {
    if (!utcTimestamp) return '';

    try {
        const date = new Date(utcTimestamp);

        // Default formatting options
        const formatOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZone: timezone || 'UTC',
            ...options
        };

        return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
    } catch (error) {
        console.error('Error converting timezone:', error);
        // Fallback to UTC if timezone conversion fails
        return new Date(utcTimestamp).toISOString();
    }
}

/**
 * Get timezone offset string (e.g., 'UTC+1', 'UTC-5')
 * @param {string} timezone - IANA timezone string
 * @returns {string} Timezone offset string
 */
export function getTimezoneOffset(timezone = 'UTC') {
    try {
        const date = new Date();
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'short'
        });

        const parts = formatter.formatToParts(date);
        const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value || timezone;

        return timeZoneName;
    } catch (error) {
        console.error('Error getting timezone offset:', error);
        return 'UTC';
    }
}

/**
 * Format date with timezone information
 * @param {string|Date} timestamp - Timestamp to format
 * @param {string} timezone - IANA timezone string
 * @param {boolean} showTimezone - Whether to show timezone info
 * @returns {string} Formatted date string
 */
export function formatWithTimezone(timestamp, timezone = 'UTC', showTimezone = true) {
    if (!timestamp) return '';

    try {
        const formattedDate = convertToLocalTimezone(timestamp, timezone);

        if (showTimezone) {
            const tzOffset = getTimezoneOffset(timezone);
            return `${formattedDate} (${tzOffset})`;
        }

        return formattedDate;
    } catch (error) {
        console.error('Error formatting with timezone:', error);
        return new Date(timestamp).toLocaleString();
    }
}

/**
 * Get relative time string (e.g., '5 minutes ago', '2 hours ago')
 * This works regardless of timezone since it's relative
 * @param {string|Date} timestamp - Timestamp
 * @returns {string} Relative time string
 */
export function getRelativeTime(timestamp) {
    if (!timestamp) return '';

    try {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);

        if (diffSec < 60) return `${diffSec} second${diffSec !== 1 ? 's' : ''} ago`;
        if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
        if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
        if (diffDay < 30) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;

        return date.toLocaleDateString();
    } catch (error) {
        console.error('Error calculating relative time:', error);
        return '';
    }
}

/**
 * Format timestamp for analytics charts (short format)
 * @param {string|Date} timestamp - Timestamp
 * @param {string} timezone - IANA timezone string
 * @returns {string} Short formatted date
 */
export function formatChartTimestamp(timestamp, timezone = 'UTC') {
    if (!timestamp) return '';

    try {
        return convertToLocalTimezone(timestamp, timezone, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (error) {
        console.error('Error formatting chart timestamp:', error);
        return new Date(timestamp).toLocaleDateString();
    }
}
